import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { CtaButton } from "@/components/brand/CtaButton";
import { NAV } from "@/content/navigation";
import { EASE } from "@/components/motion/Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const DesktopNav = () => (
  <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
    {NAV.map((n) => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.to === "/"}
        data-testid={`nav-link-${slug(n.label)}`}
        className={({ isActive }) =>
          `relative py-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
            isActive ? "text-gold-light" : "text-white/75 hover:text-white"
          }`
        }
      >
        {({ isActive }) => (
          <>
            {n.label}
            <span className={`absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-gold transition-opacity duration-300 ${isActive ? "opacity-100" : "opacity-0"}`} />
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

const MobileMenu = ({ open, close }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        key="menu"
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.6, ease: EASE }}
        className="grain fixed inset-0 z-40 flex flex-col bg-plum-deep px-5 pb-8 pt-28 sm:px-8 xl:hidden"
        data-testid="mobile-menu"
      >
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto" aria-label="Mobile">
          {NAV.map((n, i) => (
            <motion.div key={n.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.6, ease: EASE }}>
              <NavLink
                to={n.to}
                end={n.to === "/"}
                onClick={close}
                data-testid={`mobile-nav-link-${slug(n.label)}`}
                className={({ isActive }) => `flex items-baseline gap-4 border-b border-white/10 py-3.5 font-display text-3xl font-light ${isActive ? "text-gold-light" : "text-white"}`}
              >
                <span className="font-mono text-[11px] text-gold/80">0{i + 1}</span>
                {n.full || n.label}
              </NavLink>
            </motion.div>
          ))}
        </nav>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <CtaButton to="/contact" onClick={close} testid="mobile-get-started-btn">Get Started</CtaButton>
          <CtaButton to="/partnerships" onClick={close} variant="outline" testid="mobile-partner-btn">Partner With Us</CtaButton>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled || open ? "border-b border-white/10 bg-ink/85 py-3 backdrop-blur-md" : "border-b border-transparent py-5"
        }`}
        data-testid="site-header"
      >
        <div className="shell flex items-center justify-between gap-6">
          <Logo onClick={() => setOpen(false)} />
          <DesktopNav />
          <div className="flex items-center gap-3">
            <NavLink to="/partnerships" data-testid="header-partner-link" className="hidden text-[12px] font-semibold uppercase tracking-[0.16em] text-white/80 transition-colors hover:text-gold-light 2xl:inline">
              Partner With Us
            </NavLink>
            <CtaButton to="/contact" testid="header-get-started-btn" className="hidden min-h-[42px] px-5 sm:inline-flex">Get Started</CtaButton>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold xl:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={open} close={() => setOpen(false)} />
    </>
  );
};
